/**
 * ReviseRecompletionBody — renders the expanded detail for a
 * `revise_recompletion` version.
 *
 * Backend audit (Commit 9 audit step):
 *   diff = {
 *     kind: 'revise_recompletion',
 *     target_agent: string | null,
 *     agents_changed: string[],
 *     per_agent_summary: [{agent_name, added, updated, removed}, …],
 *     baseline_version_before_revise: number,
 *   }
 *
 *   Verified at version_service.py:572-593. per_agent_summary is
 *   built by _diff_agent_progress (lines 621+) and includes EVERY
 *   agent in the array_keys map — non-changed agents have all-zero
 *   counts. agents_changed is the subset filtered to those with
 *   any non-zero count.
 *
 * Render:
 *   - Caption row with target_agent + baseline reference.
 *   - Pipeline strip: one mini-station per non-zero agent, with
 *     the target agent highlighted. Each station shows +/~/-
 *     counts. Stations join with arrows in workflow order
 *     (sequence implicit by per_agent_summary order, which mirrors
 *     the array_keys map = ISO 21434 order).
 *   - Full per-agent table including zero-count agents, so a
 *     reader can confirm what didn't change.
 *
 *   Falls through to an empty-state line when per_agent_summary is
 *   empty (no agent changed — should be rare since the backend
 *   only writes this op when total_changes > 0).
 */
import React from 'react';
import {
  VersionRecord,
  ReviseRecompletionDiff,
  PerAgentReviseSummary,
} from '../../state/types';
import { agentDisplayName } from '../../utils/operationMeta';
import styles from './operationBodies.module.css';
import bodyStyles from './ReviseRecompletionBody.module.css';

interface ReviseRecompletionBodyProps {
  version: VersionRecord;
}

function hasAnyChange(r: PerAgentReviseSummary): boolean {
  return (r.added || 0) + (r.updated || 0) + (r.removed || 0) > 0;
}

export default function ReviseRecompletionBody({
  version,
}: ReviseRecompletionBodyProps) {
  const diff = version.diff as ReviseRecompletionDiff | null;
  if (!diff || diff.kind !== 'revise_recompletion') return null;

  const summary = diff.per_agent_summary ?? [];
  const changedRows = summary.filter(hasAnyChange);

  return (
    <div className={styles.body}>
      <div className={styles.detailHeader}>
        {diff.target_agent && (
          <span className={bodyStyles.targetCaption}>
            <span className={bodyStyles.targetLabel}>Target:</span>
            {agentDisplayName(diff.target_agent)}
          </span>
        )}
        <span className={bodyStyles.baselineRef}>
          Baseline before revise:{' '}
          <code>v{diff.baseline_version_before_revise}</code>
        </span>
      </div>

      {changedRows.length === 0 ? (
        <div className={bodyStyles.empty}>No downstream changes recorded.</div>
      ) : (
        <>
          <div className={bodyStyles.pipeline}>
            {changedRows.map((r, i) => {
              const isTarget = r.agent_name === diff.target_agent;
              return (
                <React.Fragment key={r.agent_name}>
                  <div
                    className={`${bodyStyles.station} ${
                      isTarget ? bodyStyles.stationTarget : ''
                    }`}
                  >
                    <div className={bodyStyles.stationAgent}>
                      {agentDisplayName(r.agent_name)}
                    </div>
                    <div className={bodyStyles.stationDelta}>
                      {r.added > 0 && (
                        <span className={bodyStyles.add}>+{r.added}</span>
                      )}
                      {r.updated > 0 && (
                        <span className={bodyStyles.upd}>~{r.updated}</span>
                      )}
                      {r.removed > 0 && (
                        <span className={bodyStyles.del}>−{r.removed}</span>
                      )}
                    </div>
                  </div>
                  {i < changedRows.length - 1 && (
                    <span className={bodyStyles.arrow}>→</span>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className={styles.subHeading}>Per-agent counts</div>
          <div className={bodyStyles.tableWrap}>
            <table className={bodyStyles.table}>
              <thead>
                <tr>
                  <th>Agent</th>
                  <th className={bodyStyles.numCell}>Added</th>
                  <th className={bodyStyles.numCell}>Updated</th>
                  <th className={bodyStyles.numCell}>Removed</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((r) => (
                  <tr key={r.agent_name}>
                    <td>{agentDisplayName(r.agent_name)}</td>
                    <td className={bodyStyles.numCell}>{r.added}</td>
                    <td className={bodyStyles.numCell}>{r.updated}</td>
                    <td className={bodyStyles.numCell}>{r.removed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
